if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.submitButton = this.querySelector('[data-sticky-atc-submit]');
      this.priceTarget = this.querySelector('[data-sticky-atc-price]');
      this.variantTarget = this.querySelector('[data-sticky-atc-variant]');
      this.messageTarget = this.querySelector('[data-sticky-delivery-message] .sticky-atc-panel__message-text');

      this.formId = this.dataset.productFormId;
      this.mainForm = document.getElementById(this.formId);

      this.boundSyncFromMainForm = this.syncFromMainForm.bind(this);
      this.boundHandleSubmit = this.handleSubmit.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleStickyQuantityChange = this.handleStickyQuantityChange.bind(this);

      this.mainSubmitObserver = null;
      this.messageInterval = null;
    }

    connectedCallback() {
      if (!this.mainForm || !this.submitButton) return;

      this.submitButton.addEventListener('click', this.boundHandleSubmit);
      this.mainForm.addEventListener('change', this.boundSyncFromMainForm);
      this.mainForm.addEventListener('input', this.boundSyncFromMainForm);

      this.addEventListener('change', this.boundHandleStickyQuantityChange);
      this.addEventListener('input', this.boundHandleStickyQuantityChange);

      document.addEventListener('on:variant:change', this.boundHandleVariantChange);

      this.observeMainSubmitButton();
      this.syncFromMainForm();
      this.syncQuantityFromMainForm();
      this.updateDeliveryMessage();
      this.startMessageTimer();
    }

    disconnectedCallback() {
      this.submitButton?.removeEventListener('click', this.boundHandleSubmit);
      this.mainForm?.removeEventListener('change', this.boundSyncFromMainForm);
      this.mainForm?.removeEventListener('input', this.boundSyncFromMainForm);

      this.removeEventListener('change', this.boundHandleStickyQuantityChange);
      this.removeEventListener('input', this.boundHandleStickyQuantityChange);

      document.removeEventListener('on:variant:change', this.boundHandleVariantChange);

      if (this.mainSubmitObserver) {
        this.mainSubmitObserver.disconnect();
        this.mainSubmitObserver = null;
      }

      if (this.messageInterval) {
        clearInterval(this.messageInterval);
        this.messageInterval = null;
      }
    }

    observeMainSubmitButton() {
      const mainSubmit = this.getMainSubmitButton();
      if (!mainSubmit) return;

      if (this.mainSubmitObserver) {
        this.mainSubmitObserver.disconnect();
      }

      this.mainSubmitObserver = new MutationObserver(() => {
        this.syncSubmitState();
      });

      this.mainSubmitObserver.observe(mainSubmit, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-disabled']
      });
    }

    getMainSubmitButton() {
      return this.mainForm?.querySelector('[name="add"]');
    }

    handleVariantChange() {
      setTimeout(() => {
        this.observeMainSubmitButton();
        this.syncFromMainForm();
        this.syncQuantityFromMainForm();
      }, 50);
    }

    getProductRoot() {
      return document.querySelector(`.js-product[data-section-id="${this.dataset.sectionId}"]`);
    }

    getSourceSummary() {
      const productRoot = this.getProductRoot();
      return productRoot?.querySelector('.product-info__price');
    }

    getSourceVariant() {
      const productRoot = this.getProductRoot();
      return productRoot?.querySelector('[data-sticky-source-title]');
    }

    syncSubmitState() {
      const mainSubmit = this.getMainSubmitButton();
      if (!mainSubmit || !this.submitButton) return;

      this.submitButton.disabled = mainSubmit.disabled;

      if (mainSubmit.hasAttribute('aria-disabled')) {
        this.submitButton.setAttribute('aria-disabled', mainSubmit.getAttribute('aria-disabled'));
      } else {
        this.submitButton.removeAttribute('aria-disabled');
      }

      this.submitButton.classList.toggle('is-loading', mainSubmit.classList.contains('is-loading'));
      this.submitButton.classList.toggle('is-success', mainSubmit.classList.contains('is-success'));

      const mainLabel = mainSubmit.textContent?.trim();
      if (mainLabel) {
        this.submitButton.textContent = mainLabel;
      }
    }

    syncFromMainForm() {
      if (!this.mainForm) return;

      const sourceSummary = this.getSourceSummary();
      const sourceVariant = this.getSourceVariant();

      this.syncSubmitState();

      if (sourceSummary && this.priceTarget) {
        this.priceTarget.innerHTML = sourceSummary.innerHTML;
      }

      if (sourceVariant && this.variantTarget) {
        this.variantTarget.textContent = sourceVariant.textContent.trim();
      }
    }

    getStickyQuantityInput() {
      return this.querySelector('.sticky-atc-panel__qty input[name="quantity"]');
    }

    getMainQuantityInput() {
      return this.mainForm?.querySelector('input[name="quantity"]');
    }

    syncQuantityFromMainForm() {
      const stickyQtyInput = this.getStickyQuantityInput();
      const mainQtyInput = this.getMainQuantityInput();

      if (!stickyQtyInput || !mainQtyInput) return;

      stickyQtyInput.value = mainQtyInput.value || 1;
    }

    handleStickyQuantityChange(event) {
      const stickyQtyInput = this.getStickyQuantityInput();
      const mainQtyInput = this.getMainQuantityInput();

      if (!stickyQtyInput || !mainQtyInput) return;
      if (event.target !== stickyQtyInput) return;

      mainQtyInput.value = stickyQtyInput.value;
      mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    handleSubmit() {
      const mainSubmit = this.getMainSubmitButton();
      if (!mainSubmit || mainSubmit.disabled) return;

      const stickyQtyInput = this.getStickyQuantityInput();
      const mainQtyInput = this.getMainQuantityInput();

      if (stickyQtyInput && mainQtyInput) {
        mainQtyInput.value = stickyQtyInput.value;
        mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        mainQtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      const invalidField = this.mainForm.querySelector(':invalid');
      if (invalidField) {
        invalidField.reportValidity();
        invalidField.focus();
        return;
      }

      if (typeof this.mainForm.requestSubmit === 'function') {
        this.mainForm.requestSubmit(mainSubmit);
      } else {
        this.mainForm.submit();
      }

      this.syncSubmitState();
    }

    getLondonTimeParts() {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      });

      const parts = formatter.formatToParts(now);
      const map = {};

      parts.forEach((part) => {
        if (part.type !== 'literal') {
          map[part.type] = part.value;
        }
      });

      return {
        weekday: map.weekday,
        hour: Number(map.hour),
        minute: Number(map.minute)
      };
    }

updateDeliveryMessage() {
  if (!this.messageTarget) return;

  const { weekday, hour, minute } = this.getLondonTimeParts();
  const currentMinutes = (hour * 60) + minute;

  const isParamoseDcmProduct = this.dataset.productHandle === 'paramose-dcm-paint-stripper-5l';

  const beforeCutoff = isParamoseDcmProduct
    ? currentMinutes < (15 * 60 + 30)
    : currentMinutes < (16 * 60);

  let message = isParamoseDcmProduct
    ? 'Order today before 3:30pm for delivery tomorrow.'
    : 'Order today before 4:00pm for delivery tomorrow.';

  if (weekday === 'Fri') {
    message = isParamoseDcmProduct
      ? 'Saturday delivery available at checkout if ordered before 3:30pm.'
      : 'Saturday delivery available at checkout if ordered before 4:00pm.';
  } else if (weekday === 'Sat' || weekday === 'Sun') {
    message = isParamoseDcmProduct
      ? 'Order before 3:30pm on Monday for Tuesday delivery.'
      : 'Order before 4:00pm on Monday for Tuesday delivery.';
  } else if (beforeCutoff) {
    message = isParamoseDcmProduct
      ? 'Order today before 3:30pm for delivery tomorrow.'
      : 'Order today before 4:00pm for delivery tomorrow.';
  } else {
    message = isParamoseDcmProduct
      ? 'Order before 3:30pm tomorrow for next day delivery.'
      : 'Order before 4:00pm tomorrow for next day delivery.';
  }

  this.messageTarget.textContent = message;
}

    startMessageTimer() {
      if (this.messageInterval) {
        clearInterval(this.messageInterval);
      }

      this.messageInterval = setInterval(() => {
        this.updateDeliveryMessage();
      }, 60000);
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}